import cn from 'classnames';
import LazyMotionWrapper from 'components/lazy-motion-wrapper';
import { m } from 'motion/react';
import { FC } from 'react';

const MAX_VISIBLE_DOTS = 5;
const TRUNCATION_THRESHOLD = 6;

interface DotStyle {
  backgroundColor: string;
  width: number;
  height: number;
}

// Active dot is wide (pill shape). Inactive dots shrink as they get further from the active dot:
// distance ≤ 1 → 6×6, distance = 2 → 4×4, distance ≥ 3 → 3×3 (edge indicator).
export const getDotStyle = (distance: number, isActive: boolean): DotStyle => {
  if (isActive) {
    return { backgroundColor: 'var(--color-brand-blue-700)', width: 18, height: 6 };
  }

  const backgroundColor = 'var(--color-brand-blue-100)';

  if (distance <= 1) return { backgroundColor, width: 6, height: 6 };
  if (distance === 2) return { backgroundColor, width: 4, height: 4 };
  return { backgroundColor, width: 3, height: 3 };
};

export const getVisibleWindow = (
  selectedSlideIndex: number,
  slidesLength: number
): { start: number; end: number } => {
  const half = Math.floor(MAX_VISIBLE_DOTS / 2);
  const start = Math.max(0, Math.min(selectedSlideIndex - half, slidesLength - MAX_VISIBLE_DOTS));
  const end = Math.min(slidesLength - 1, start + MAX_VISIBLE_DOTS - 1);
  return { start, end };
};

interface Props {
  className?: string;
  slidesLength: number;
  selectedSlideIndex: number;
  onClick: (index: number) => void;
}

const CarouselSelector: FC<Props> = ({ className, slidesLength, selectedSlideIndex, onClick }) => {
  if (slidesLength <= 1) return null;

  const shouldTruncate = slidesLength >= TRUNCATION_THRESHOLD;
  const { start, end } = shouldTruncate
    ? getVisibleWindow(selectedSlideIndex, slidesLength)
    : { start: 0, end: slidesLength - 1 };

  return (
    <LazyMotionWrapper>
      <div
        className={cn(
          'flex w-full items-center justify-center gap-[6px] overflow-hidden',
          className
        )}
      >
        {Array.from({ length: slidesLength }).map((_, index) => {
          if (index < start - 1 || index > end + 1) return null;

          const isActive = selectedSlideIndex === index;
          const distance = Math.abs(index - selectedSlideIndex);
          const { backgroundColor, width, height } = getDotStyle(distance, isActive);

          return (
            <m.button
              key={index}
              animate={{ backgroundColor, width, height }}
              className={cn('shrink-0 rounded-full focus:outline-hidden')}
              onClick={() => onClick(index)}
              transition={{
                damping: 20,
                duration: 0.2,
                stiffness: 500,
                type: 'spring',
              }}
            />
          );
        })}
      </div>
    </LazyMotionWrapper>
  );
};

export default CarouselSelector;
